import {
  type DutyOfCandour,
  DutyOfCandourKeyRequirementEnum,
  DutyOfCandourMethodEnum,
  DutyOfCandourPersonInformedEnum
} from '@lmc/validations'
import {
  Form as AntdForm,
  Checkbox,
  DatePicker,
  Input,
  Select,
  TimePicker
} from 'antd'
import type { CheckboxGroupProps } from 'antd/es/checkbox'
import dayjs, { type Dayjs } from 'dayjs'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { Fragment, type ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import IncidentCaseV2StepForm from '../IncidentCaseV2StepForm'
import type {
  IncidentCaseV2StepProps,
  IncidentCaseV2StepValues
} from '../incidentCaseV2Steps'
import StepField from '../StepField'
import StepNameList from '../StepNameList'
import { StepReveal } from '../StepReveal'
import { StepYesNoField } from '../StepYesNo'
import {
  DATE_FORMAT,
  StepFieldPair,
  StepSection,
  StepSeparator,
  stepChoiceColumn,
  TIME_FORMAT
} from '../stepLayout'
import { useTeamMemberOptions } from '../stepOptions'
import {
  fromDateOnlyIso,
  isBlank,
  toDateOnlyIso,
  toInstantIso,
  toNamedPeople,
  trimmed,
  withoutBlanks
} from '../stepValues'
import { useServiceUserWords } from '../useServiceUserWords'

const testId = 'incident-case-v2-duty-of-candour'

const PERSON_KEYS: Record<DutyOfCandourPersonInformedEnum, string> = {
  [DutyOfCandourPersonInformedEnum.serviceUser]:
    incidentReportsConstants.CASE_V2_CANDOUR_PERSON_SERVICE_USER,
  [DutyOfCandourPersonInformedEnum.familyOrRepresentative]:
    incidentReportsConstants.CASE_V2_CANDOUR_PERSON_FAMILY,
  [DutyOfCandourPersonInformedEnum.other]:
    incidentReportsConstants.CASE_V2_CANDOUR_OTHER
}

const METHOD_KEYS: Record<DutyOfCandourMethodEnum, string> = {
  [DutyOfCandourMethodEnum.phone]:
    incidentReportsConstants.CASE_V2_CANDOUR_METHOD_PHONE,
  [DutyOfCandourMethodEnum.inPersonMeeting]:
    incidentReportsConstants.CASE_V2_CANDOUR_METHOD_IN_PERSON_MEETING,
  [DutyOfCandourMethodEnum.written]:
    incidentReportsConstants.CASE_V2_CANDOUR_METHOD_WRITTEN,
  [DutyOfCandourMethodEnum.other]:
    incidentReportsConstants.CASE_V2_CANDOUR_OTHER
}

const REQUIREMENT_KEYS: Record<DutyOfCandourKeyRequirementEnum, string> = {
  [DutyOfCandourKeyRequirementEnum.apologyProvided]:
    incidentReportsConstants.CASE_V2_CANDOUR_REQUIREMENT_APOLOGY_PROVIDED,
  [DutyOfCandourKeyRequirementEnum.explanationGiven]:
    incidentReportsConstants.CASE_V2_CANDOUR_REQUIREMENT_EXPLANATION_GIVEN,
  [DutyOfCandourKeyRequirementEnum.supportOffered]:
    incidentReportsConstants.CASE_V2_CANDOUR_REQUIREMENT_SUPPORT_OFFERED,
  [DutyOfCandourKeyRequirementEnum.writtenFollowUpProvided]:
    incidentReportsConstants.CASE_V2_CANDOUR_REQUIREMENT_WRITTEN_FOLLOW_UP_PROVIDED
}

type DutyOfCandourValues = {
  /** The saved instant the date and time were filled from; no field of its own. */
  communicatedAt?: string
  communicatedBy?: Array<string>
  communicationDate?: Dayjs | null
  communicationNotes?: string
  communicationTime?: Dayjs | null
  completedAt?: Dayjs | null
  completedBy?: string | null
  details?: string
  familyRepresentatives?: Array<{ name?: string }>
  isApplicable?: boolean | null
  keyRequirementsMet?: Array<DutyOfCandourKeyRequirementEnum>
  methods?: Array<DutyOfCandourMethodEnum>
  otherMethod?: string
  otherPersons?: Array<{ name?: string }>
  outcomeNotes?: string
  personsInformed?: Array<DutyOfCandourPersonInformedEnum>
  responsibleUserId?: string | null
  sharedAt?: Dayjs | null
  writtenFollowUpProvidedAt?: Dayjs | null
}

type DutyOfCandourStepValues = NonNullable<
  IncidentCaseV2StepValues['dutyOfCandour']
>

/** Everything recorded under Yes, emptied. */
const NO_DETAILS: DutyOfCandourValues = {
  communicatedBy: [],
  communicationDate: null,
  communicationNotes: undefined,
  communicationTime: null,
  completedAt: null,
  completedBy: null,
  familyRepresentatives: [{ name: '' }],
  keyRequirementsMet: [],
  methods: [],
  otherMethod: undefined,
  otherPersons: [{ name: '' }],
  outcomeNotes: undefined,
  personsInformed: [],
  responsibleUserId: null,
  sharedAt: null,
  writtenFollowUpProvidedAt: null
}

/** What is recorded under Yes; empty groups are left out. */
const toDetails = (
  values: DutyOfCandourValues
): Omit<DutyOfCandourStepValues, 'details' | 'isApplicable'> => {
  // A follow-up answer is kept only while the box that asks for it is ticked.
  const isInformed = (person: DutyOfCandourPersonInformedEnum) =>
    values.personsInformed?.includes(person) ?? false
  const hasOtherMethod =
    values.methods?.includes(DutyOfCandourMethodEnum.other) ?? false
  const hasWrittenFollowUp =
    values.keyRequirementsMet?.includes(
      DutyOfCandourKeyRequirementEnum.writtenFollowUpProvided
    ) ?? false

  const communication = withoutBlanks({
    communicatedAt:
      toInstantIso(
        values.communicationDate,
        values.communicationTime,
        values.communicatedAt
      ) ?? undefined,
    communicatedBy: values.communicatedBy,
    keyRequirementsMet: values.keyRequirementsMet,
    methods: values.methods,
    notes: trimmed(values.communicationNotes),
    otherMethod: hasOtherMethod ? trimmed(values.otherMethod) : undefined,
    writtenFollowUpProvidedAt: hasWrittenFollowUp
      ? (toDateOnlyIso(values.writtenFollowUpProvidedAt) ?? undefined)
      : undefined
  })
  const outcomeShared = withoutBlanks({
    notes: trimmed(values.outcomeNotes),
    responsibleUserId: values.responsibleUserId ?? undefined,
    sharedAt: toDateOnlyIso(values.sharedAt) ?? undefined
  })

  return withoutBlanks({
    communication: Object.keys(communication).length
      ? communication
      : undefined,
    completedAt: toDateOnlyIso(values.completedAt) ?? undefined,
    completedBy: values.completedBy ?? undefined,
    familyRepresentatives: isInformed(
      DutyOfCandourPersonInformedEnum.familyOrRepresentative
    )
      ? toNamedPeople(values.familyRepresentatives)
      : undefined,
    otherPersons: isInformed(DutyOfCandourPersonInformedEnum.other)
      ? toNamedPeople(values.otherPersons)
      : undefined,
    outcomeShared: Object.keys(outcomeShared).length
      ? outcomeShared
      : undefined,
    personsInformed: values.personsInformed
  })
}

const toStepValues = (
  values: DutyOfCandourValues
): IncidentCaseV2StepValues => {
  // Unanswered: there is nothing to record yet, and Next asks for an answer.
  if (values.isApplicable === undefined || values.isApplicable === null) {
    return {}
  }
  // Details explain a No, so a Yes leaves them out.
  return {
    dutyOfCandour: values.isApplicable
      ? { isApplicable: true, ...toDetails(values) }
      : {
          isApplicable: false,
          ...withoutBlanks({ details: trimmed(values.details) })
        }
  }
}

const toInitialValues = (
  saved: DutyOfCandour | undefined
): DutyOfCandourValues => {
  const communicatedAt = saved?.communication?.communicatedAt
  const familyRepresentatives = saved?.familyRepresentatives ?? []
  const otherPersons = saved?.otherPersons ?? []

  return {
    communicatedAt,
    communicatedBy: saved?.communication?.communicatedBy ?? [],
    communicationDate: communicatedAt ? dayjs(communicatedAt) : null,
    communicationNotes: saved?.communication?.notes,
    communicationTime: communicatedAt ? dayjs(communicatedAt) : null,
    completedAt: fromDateOnlyIso(saved?.completedAt),
    completedBy: saved?.completedBy ?? null,
    details: saved?.details,
    // One empty row, so ticking Family/representative shows a name to fill in.
    familyRepresentatives: familyRepresentatives.length
      ? familyRepresentatives
      : [{ name: '' }],
    isApplicable: saved?.isApplicable,
    keyRequirementsMet: saved?.communication?.keyRequirementsMet ?? [],
    methods: saved?.communication?.methods ?? [],
    otherMethod: saved?.communication?.otherMethod,
    otherPersons: otherPersons.length ? otherPersons : [{ name: '' }],
    outcomeNotes: saved?.outcomeShared?.notes,
    personsInformed: saved?.personsInformed ?? [],
    responsibleUserId: saved?.outcomeShared?.responsibleUserId ?? null,
    sharedAt: fromDateOnlyIso(saved?.outcomeShared?.sharedAt),
    writtenFollowUpProvidedAt: fromDateOnlyIso(
      saved?.communication?.writtenFollowUpProvidedAt
    )
  }
}

/**
 * Step 8: whether Duty of Candour (Regulation 20) applies and, when it does,
 * who was told, how, what was shared afterwards and who signed it off.
 */
const DutyOfCandourStep = ({
  incidentReport,
  handleRef,
  onDirtyChange
}: IncidentCaseV2StepProps) => {
  // `dutyOfCandour` is not on the ts-common case type.
  const saved = (
    incidentReport.case as { dutyOfCandour?: DutyOfCandour } | null
  )?.dutyOfCandour
  const initialValues = useMemo(() => toInitialValues(saved), [saved])

  return (
    <IncidentCaseV2StepForm
      handleRef={handleRef}
      onDirtyChange={onDirtyChange}
      initialValues={initialValues}
      toStepValues={toStepValues}
      testId={testId}
    >
      <DutyOfCandourFields
        savedTeamMembers={[
          ...(saved?.communication?.communicatedBy ?? []),
          saved?.completedBy,
          saved?.outcomeShared?.responsibleUserId
        ]}
      />
    </IncidentCaseV2StepForm>
  )
}

type ChoiceListProps<Code extends string> = Omit<
  CheckboxGroupProps,
  'children' | 'options'
> & {
  keys: Record<Code, string>
  /** Names the group: the field's <label> can't name a div. */
  label: string
  /** Shown under a choice while it is ticked. */
  reveals?: Partial<Record<Code, ReactNode>>
  /** Interpolated into the labels. */
  variable?: string
}

/** A column of ticks, each followed by what it reveals while ticked. */
const ChoiceList = <Code extends string>({
  keys,
  label,
  reveals = {},
  variable,
  ...group
}: ChoiceListProps<Code>) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  return (
    <Checkboxes {...group}>
      <Choices role="group" aria-label={label}>
        {(Object.entries(keys) as Array<[Code, string]>).map(([code, key]) => (
          <Fragment key={code}>
            <Checkbox value={code}>{t(key, { variable })}</Checkbox>
            {group.value?.includes(code) && reveals[code]}
          </Fragment>
        ))}
      </Choices>
    </Checkboxes>
  )
}

/** The fields, inside the step's form so they can follow its answers. */
const DutyOfCandourFields = ({
  savedTeamMembers
}: {
  savedTeamMembers: Array<string | undefined>
}) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const form = AntdForm.useFormInstance<DutyOfCandourValues>()
  const isApplicable = AntdForm.useWatch('isApplicable', form)
  const { singular } = useServiceUserWords()
  const teamMemberOptions = useTeamMemberOptions({ saved: savedTeamMembers })
  const placeholder = t(incidentReportsConstants.CASE_V2_SELECT_PLACEHOLDER)
  const selectAllThatApply = t(
    incidentReportsConstants.CASE_V2_CANDOUR_SELECT_ALL_THAT_APPLY
  )

  /** A date and time are saved as one instant, so each needs the other. */
  const needsPartner = (partner: keyof DutyOfCandourValues) => ({
    validator: (_: unknown, value: unknown) =>
      isBlank(value) && !isBlank(form.getFieldValue(partner))
        ? Promise.reject(
            new Error(t(incidentReportsConstants.CASE_V2_FIELD_REQUIRED))
          )
        : Promise.resolve()
  })

  return (
    <>
      <StepSection>
        <StepYesNoField
          confirmNo={{
            body: t(incidentReportsConstants.CASE_V2_CANDOUR_CLEAR_BODY),
            isNeeded: () =>
              Object.keys(toDetails(form.getFieldsValue(true))).length > 0,
            onConfirm: () => form.setFieldsValue(NO_DETAILS),
            title: t(incidentReportsConstants.CASE_V2_CANDOUR_CLEAR_TITLE)
          }}
          detailsLabel={t(incidentReportsConstants.CASE_V2_CANDOUR_DETAILS)}
          detailsName="details"
          detailsTestId={`${testId}-details`}
          label={t(incidentReportsConstants.CASE_V2_CANDOUR_APPLICABLE)}
          name="isApplicable"
          testId={`${testId}-applicable`}
        >
          <StepField
            name="personsInformed"
            isRequired
            label={t(incidentReportsConstants.CASE_V2_CANDOUR_PERSON_INFORMED)}
            hint={selectAllThatApply}
          >
            <ChoiceList
              data-testid={`${testId}-persons-informed`}
              label={t(
                incidentReportsConstants.CASE_V2_CANDOUR_PERSON_INFORMED
              )}
              keys={PERSON_KEYS}
              reveals={{
                [DutyOfCandourPersonInformedEnum.familyOrRepresentative]: (
                  <StepReveal>
                    <StepNameList
                      name="familyRepresentatives"
                      addLabel={t(
                        incidentReportsConstants.CASE_V2_CANDOUR_ADD_PERSON
                      )}
                      testId={`${testId}-family`}
                    />
                  </StepReveal>
                ),
                [DutyOfCandourPersonInformedEnum.other]: (
                  <StepReveal>
                    <StepNameList
                      name="otherPersons"
                      addLabel={t(
                        incidentReportsConstants.CASE_V2_CANDOUR_ADD_PERSON
                      )}
                      testId={`${testId}-other-persons`}
                    />
                  </StepReveal>
                )
              }}
              variable={singular}
            />
          </StepField>
        </StepYesNoField>
      </StepSection>

      {isApplicable && (
        <>
          <StepSeparator />

          <StepSection
            title={t(
              incidentReportsConstants.CASE_V2_CANDOUR_COMMUNICATION_DETAILS
            )}
          >
            <StepFieldPair>
              <StepField
                name="communicationDate"
                dependencies={['communicationTime']}
                rules={[needsPartner('communicationTime')]}
                label={t(incidentReportsConstants.CASE_V2_CANDOUR_DATE)}
              >
                <DatePicker
                  data-testid={`${testId}-communication-date`}
                  format={DATE_FORMAT}
                />
              </StepField>
              <StepField
                name="communicationTime"
                dependencies={['communicationDate']}
                rules={[needsPartner('communicationDate')]}
                label={t(incidentReportsConstants.CASE_V2_CANDOUR_TIME)}
              >
                <TimePicker
                  data-testid={`${testId}-communication-time`}
                  format={TIME_FORMAT}
                />
              </StepField>
            </StepFieldPair>
            <StepField
              name="methods"
              label={t(incidentReportsConstants.CASE_V2_CANDOUR_METHOD)}
              hint={selectAllThatApply}
            >
              <ChoiceList
                data-testid={`${testId}-methods`}
                label={t(incidentReportsConstants.CASE_V2_CANDOUR_METHOD)}
                keys={METHOD_KEYS}
                reveals={{
                  [DutyOfCandourMethodEnum.other]: (
                    <StepReveal>
                      <StepField
                        name="otherMethod"
                        label={t(
                          incidentReportsConstants.CASE_V2_CANDOUR_OTHER_METHOD
                        )}
                      >
                        <Input data-testid={`${testId}-other-method`} />
                      </StepField>
                    </StepReveal>
                  )
                }}
              />
            </StepField>
            <StepField
              name="communicatedBy"
              label={t(
                incidentReportsConstants.CASE_V2_CANDOUR_COMMUNICATED_BY
              )}
            >
              <Select
                data-testid={`${testId}-communicated-by`}
                mode="multiple"
                allowClear
                options={teamMemberOptions}
                placeholder={t(
                  incidentReportsConstants.CASE_V2_SELECT_PLACEHOLDER_MULTI
                )}
                optionFilterProp="label"
              />
            </StepField>
            <StepField
              name="keyRequirementsMet"
              label={t(
                incidentReportsConstants.CASE_V2_CANDOUR_KEY_REQUIREMENTS
              )}
            >
              <ChoiceList
                data-testid={`${testId}-key-requirements`}
                label={t(
                  incidentReportsConstants.CASE_V2_CANDOUR_KEY_REQUIREMENTS
                )}
                keys={REQUIREMENT_KEYS}
                reveals={{
                  [DutyOfCandourKeyRequirementEnum.writtenFollowUpProvided]: (
                    <StepReveal>
                      <StepField
                        name="writtenFollowUpProvidedAt"
                        label={t(
                          incidentReportsConstants.CASE_V2_CANDOUR_DATE_PROVIDED
                        )}
                      >
                        <DatePicker
                          data-testid={`${testId}-written-follow-up-at`}
                          format={DATE_FORMAT}
                        />
                      </StepField>
                    </StepReveal>
                  )
                }}
              />
            </StepField>
            <StepField
              name="communicationNotes"
              label={t(incidentReportsConstants.CASE_V2_CANDOUR_NOTES)}
              hint={t(
                incidentReportsConstants.CASE_V2_CANDOUR_COMMUNICATION_NOTES_HINT
              )}
            >
              <Input.TextArea
                data-testid={`${testId}-communication-notes`}
                autoSize={{ minRows: 3 }}
              />
            </StepField>
          </StepSection>

          <StepSeparator />

          <StepSection
            title={t(incidentReportsConstants.CASE_V2_CANDOUR_OUTCOME_SHARED)}
          >
            <StepField
              name="sharedAt"
              label={t(incidentReportsConstants.CASE_V2_CANDOUR_DATE_SHARED)}
            >
              <DatePicker
                data-testid={`${testId}-shared-at`}
                format={DATE_FORMAT}
              />
            </StepField>
            <StepField
              name="responsibleUserId"
              label={t(
                incidentReportsConstants.CASE_V2_CANDOUR_PERSON_RESPONSIBLE
              )}
              hint={t(
                incidentReportsConstants.CASE_V2_CANDOUR_PERSON_RESPONSIBLE_HINT
              )}
            >
              <Select
                data-testid={`${testId}-responsible`}
                allowClear
                showSearch
                options={teamMemberOptions}
                placeholder={placeholder}
                optionFilterProp="label"
              />
            </StepField>
            <StepField
              name="outcomeNotes"
              label={t(incidentReportsConstants.CASE_V2_CANDOUR_NOTES)}
              hint={t(
                incidentReportsConstants.CASE_V2_CANDOUR_OUTCOME_NOTES_HINT
              )}
            >
              <Input.TextArea
                data-testid={`${testId}-outcome-notes`}
                autoSize={{ minRows: 3 }}
              />
            </StepField>
          </StepSection>

          <StepSection>
            <StepField
              name="completedBy"
              isRequired
              label={t(incidentReportsConstants.CASE_V2_CANDOUR_COMPLETED_BY)}
            >
              <Select
                data-testid={`${testId}-completed-by`}
                showSearch
                options={teamMemberOptions}
                placeholder={placeholder}
                optionFilterProp="label"
              />
            </StepField>
            <StepField
              name="completedAt"
              label={t(incidentReportsConstants.CASE_V2_CANDOUR_DATE_COMPLETED)}
            >
              <DatePicker
                data-testid={`${testId}-completed-at`}
                format={DATE_FORMAT}
              />
            </StepField>
          </StepSection>
        </>
      )}
    </>
  )
}

const Checkboxes = styled(Checkbox.Group)`
  ${stepChoiceColumn}
`

const Choices = styled.div`
  ${stepChoiceColumn}
`

export default DutyOfCandourStep
